import {Coach, UserRoleEnum} from "@/interfaces/interface";
import {Form, ItemContainer, Spinner} from "@shared";
import "./AdminCoachesList.scss";
import {EmptyItemContainer} from "@/shared/EmptyItemContainer/EmptyItemContainer";
import {useContext, useEffect, useRef} from "react";
import {ModalContext} from "@/contexts";
import {EditCoachModal} from "@/components/Modals/EditCoachModal/EditCoachModal";
import {AddSuperAdminCoachModal} from "@/components/Modals/AddSuperAdminCoachModal/AddSuperAdminCoachModal";
import Api from "@/api/api";

type Props = {
    setSelectedCoach: (coach: Coach) => void;
    coaches?: Coach[];
    coachesCount: number;
    coachesSearch: string;
    coachesLoading: boolean;
    handleCoachesSearch: (search: string) => void;
    loadMoreCoaches: () => void;
    refetchCoaches: () => void;
    selectedOrganizationID: string;
    updateClients: () => Promise<void>;
};

export function AdminCoachesList({
                                     setSelectedCoach,
                                     coaches,
                                     coachesCount,
                                     coachesSearch,
                                     coachesLoading,
                                     handleCoachesSearch,
                                     loadMoreCoaches,
                                     refetchCoaches,
                                     selectedOrganizationID,
                                     updateClients
                                 }: Props) {
    const {openModal} = useContext(ModalContext);
    const listRef = useRef<HTMLDivElement>(null);

    const handleAddCoach = () => {
        openModal(
            <AddSuperAdminCoachModal
                selectedOrganizationID={selectedOrganizationID}
                updateCoaches={refetchCoaches}
            />
        );
    };

    const onTableItemClick = async (username: string) => {
        const neededCoach = coaches?.find((coach) => coach.username === username);

        if (neededCoach) {
            const response = await Api.get<any>(
                `/admin/users/${neededCoach.username}`
            );
            if (response.data) {
                const isOrgAdminRoleExist = response.data.roles.find((el: string) => el === UserRoleEnum.ROLE_ORGANIZATION_ADMIN);
                if (isOrgAdminRoleExist) {
                    neededCoach.role = 'Organization Admin';
                } else {
                    neededCoach.role = 'Coach';
                }
            }
            setSelectedCoach(neededCoach);
            openModal(<EditCoachModal updateClients={updateClients} selectedOrganizationID={selectedOrganizationID} coach={neededCoach}/>);
        }
    };

    // Handle scroll for lazy loading
    const handleScroll = () => {
        if (listRef.current) {
            const { scrollTop, scrollHeight, clientHeight } = listRef.current;
            if (scrollTop + clientHeight >= scrollHeight - 10 && !coachesLoading) {
                loadMoreCoaches();
            }
        }
    };

    useEffect(() => {
        const listElement = listRef.current;
        if (listElement) {
            listElement.addEventListener('scroll', handleScroll);
            return () => listElement.removeEventListener('scroll', handleScroll);
        }
    }, [coachesLoading, coaches]);

    const areSomeCoachExist = coaches && coaches.length > 0;
    const hasMore = coaches && coaches.length < coachesCount;


    return (
    <ItemContainer className={`admin-athletes-list`}>
      <ItemContainer.Header
        className="admin-coaches-list__header"
        leftSide={<div>{`Coaches (${coachesCount})`}</div>}
        rightSide={
          <>
            <Form.Button
              onClick={handleAddCoach}
              hidden={!selectedOrganizationID}
            >
              {/*<ImageFromAssets name="cloud-computing.svg"/>*/}
              ADD COACH
            </Form.Button>
          </>
        }
      />

      <div className="admin-coaches-list__search" style={{padding: "10px"}}>
        <Form.Input
          placeholder="Search coaches..."
          value={coachesSearch}
          onChange={(e) => handleCoachesSearch(e.target.value)}
        />
      </div>

      <div
        ref={listRef}
        className="admin-coaches-list__body"
        style={{height: "calc(100% - 190px)", overflowY: "auto"}}
      >
        {areSomeCoachExist && (
          <div className="admin-coaches-list__body__list">
            {coaches.map((coach, index) => {
              const {name = "", email = "", username = '', userInfo: {firstName, lastName}} = coach || {};
              return (
                <div
                  key={username + index}
                  className={`admin-coaches-list__body__list__item`}
                  onClick={() => onTableItemClick(username)}
                >
                  <div className="name">{firstName + ' ' + lastName}</div>
                  <div className="email">{email}</div>
                </div>
              );
            })}
            {coachesLoading && (
              <div style={{display: "flex", justifyContent: "center", padding: "20px"}}>
                <Spinner />
              </div>
            )}
            {!coachesLoading && hasMore && (
              <div style={{textAlign: "center", padding: "10px", color: "#888"}}>
                Scroll for more...
              </div>
            )}
          </div>
        )}

        {!areSomeCoachExist && !coachesLoading && (
          <EmptyItemContainer text="There are no coaches"/>
        )}

        {!areSomeCoachExist && coachesLoading && (
          <div style={{display: "flex", justifyContent: "center", padding: "20px"}}>
            <Spinner />
          </div>
        )}
      </div>
    </ItemContainer>
  );
}
